import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Users2, Search, Loader2, AlertTriangle } from "lucide-react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AdminGroupDetail, STATE_LABEL, stateVariant, fmt } from "@/components/admin/groups/AdminGroupDetail";

const STATES = ["all", "active", "draft", "archived", "suspended", "pending_deletion", "deleted"];
const PAGE = 25;

/** Admin Console → Groups. Platform-staff Group administration on the EXISTING
 *  /api/admin/groups endpoints and their `require_moderator` authorization. */
export default function AdminGroups() {
  const [qInput, setQInput] = React.useState("");
  const [q, setQ] = React.useState("");
  const [state, setState] = React.useState("all");
  const [page, setPage] = React.useState(0);
  const [openId, setOpenId] = React.useState(null);

  const list = useQuery({
    queryKey: ["admin-groups", q, state, page],
    queryFn: async () => (await api.get("/admin/groups", {
      params: { q: q || undefined, state, limit: PAGE, offset: page * PAGE },
    })).data,
  });
  const queue = useQuery({
    queryKey: ["admin-groups-purge-queue"],
    queryFn: async () => (await api.get("/admin/groups/purge/preview")).data,
  });

  const items = list.data?.items || [];
  const total = list.data?.total || 0;

  return (
    <div className="space-y-5" data-testid="admin-groups">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-[680]">
            <Users2 className="h-6 w-6 text-primary" />Groups
          </h1>
          <p className="text-sm text-muted-foreground">
            Find any group in any state, enforce, recover ownership and run the deletion queue.
          </p>
        </div>
        <Badge variant="secondary" data-testid="admin-groups-total">{total} groups</Badge>
      </div>

      <Card className="flex flex-wrap items-center justify-between gap-3 border-0 p-4"
            data-testid="admin-groups-purge-queue">
        <div className="text-sm">
          <p className="font-[640]">Deletion queue</p>
          <p className="text-muted-foreground">
            {queue.isLoading ? "Loading…" : (
              <>
                <span data-testid="admin-groups-queue-eligible">{queue.data?.eligible ?? 0}</span> ready to purge ·{" "}
                <span data-testid="admin-groups-queue-held">{queue.data?.held ?? 0}</span> held by enforcement ·{" "}
                {queue.data?.recovery_days ?? 30}-day window
              </>
            )}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {(queue.data?.items || []).slice(0, 3).map((it) => (
            <Button key={it.group_id} size="sm" variant="outline"
                    data-testid={`admin-groups-queue-open-${it.group_id}`}
                    onClick={() => setOpenId(it.group_id)}>
              {it.display_name} · due {fmt(it.purge_at)}
            </Button>
          ))}
          {(queue.data?.held_items || []).slice(0, 2).map((it) => (
            <Badge key={it.group_id} variant="destructive" className="gap-1">
              <AlertTriangle className="h-3 w-3" />{it.display_name}: {it.hold?.reason}
            </Badge>
          ))}
        </div>
      </Card>

      <div className="flex flex-wrap items-center gap-2">
        <form className="flex flex-1 items-center gap-2" onSubmit={(e) => { e.preventDefault(); setPage(0); setQ(qInput.trim()); }}>
          <Input value={qInput} onChange={(e) => setQInput(e.target.value)}
                 placeholder="Search name or handle…" data-testid="admin-groups-search-input" />
          <Button type="submit" size="sm" data-testid="admin-groups-search">
            <Search className="h-4 w-4" />
          </Button>
        </form>
        <Select value={state} onValueChange={(v) => { setPage(0); setState(v); }}>
          <SelectTrigger className="w-[190px]" data-testid="admin-groups-state-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {STATES.map((s) => (
              <SelectItem key={s} value={s}>{s === "all" ? "All states" : STATE_LABEL[s]}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {list.isLoading ? (
        <Loader2 className="h-5 w-5 animate-spin" />
      ) : items.length === 0 ? (
        <Card className="border-0 p-8 text-center text-sm text-muted-foreground" data-testid="admin-groups-empty">
          No groups match.
        </Card>
      ) : (
        <div className="space-y-2" data-testid="admin-groups-list">
          {items.map((g) => (
            <Card key={g.id} className="flex flex-wrap items-center justify-between gap-3 border-0 p-4"
                  data-testid={`admin-group-row-${g.id}`}>
              <div className="min-w-0 space-y-1">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="truncate font-[640]">{g.display_name || "Untitled group"}</p>
                  <Badge variant={stateVariant(g.state)} data-testid={`admin-group-state-${g.id}`}>
                    {STATE_LABEL[g.state] || g.state}
                  </Badge>
                  {g.moderation_hold ? <Badge variant="destructive">Hold</Badge> : null}
                  {g.ownership_recovery_required ? <Badge variant="destructive">Needs owner</Badge> : null}
                </div>
                <p className="text-xs text-muted-foreground">
                  {g.slug ? `@${g.slug}` : "no handle"} · {g.member_count ?? 0} members
                  {g.purge_at ? ` · deletes ${fmt(g.purge_at)}` : ""}
                </p>
              </div>
              <Button size="sm" variant="outline" onClick={() => setOpenId(g.id)}
                      data-testid={`admin-group-manage-${g.id}`}>
                Manage
              </Button>
            </Card>
          ))}
        </div>
      )}

      {total > PAGE ? (
        <div className="flex items-center justify-between">
          <Button size="sm" variant="ghost" disabled={page === 0} onClick={() => setPage((p) => p - 1)}
                  data-testid="admin-groups-prev">Previous</Button>
          <span className="text-xs text-muted-foreground">
            {page * PAGE + 1}–{Math.min((page + 1) * PAGE, total)} of {total}
          </span>
          <Button size="sm" variant="ghost" disabled={(page + 1) * PAGE >= total}
                  onClick={() => setPage((p) => p + 1)} data-testid="admin-groups-next">Next</Button>
        </div>
      ) : null}

      <AdminGroupDetail groupId={openId} onClose={() => setOpenId(null)} />
    </div>
  );
}
